import { useMemo } from 'react';
import {
  StorageType,
  useSessionStorage,
  useLocalStorage,
  removeItem,
  setItem,
  getItem,
} from 'app/utils/StorageContext';
import { notify } from 'app/services/errorReporter';
import FluentdLogger from 'app/services/fluentdLogger';

export interface IValueParser<ValueType> {
  serialize: (value: ValueType) => unknown | null;
  deserialize: (stored: unknown | null) => ValueType;
  isEqual?: (a: ValueType, b: ValueType) => boolean;
}

/**
 * Creates a hook to access values stored in local and session storage.
 * @param key string key used to access value
 * @param valueParser parser used to serialize and deserialize value
 * @param storageType whether the value is stored in local or session storage
 */
export function createStorageHook<ValueType>(
  key: string,
  valueParser: IValueParser<ValueType>,
  storageType: StorageType,
) {
  const isEqual = valueParser.isEqual || ((a: unknown, b: unknown): boolean => a === b);
  const useStorage = storageType === StorageType.LOCAL ? useLocalStorage : useSessionStorage;

  const useStorageHook: () => [ValueType, (newValue: ValueType) => void] = () => {
    const storage = useStorage();
    const cachedValue = getItem(storage, key);
    const deserializedValue = useMemo(() => {
      try {
        if (cachedValue) {
          const parsedValue = JSON.parse(cachedValue);
          return valueParser.deserialize(parsedValue);
        }
      } catch (error) {
        FluentdLogger.info({
          name: `Storage.couldntParseValue`,
          message: error.message,
        });
        removeItem(storage, key);
      }
      return valueParser.deserialize(null);
    }, [cachedValue, storage]);

    return ([
      deserializedValue,
      (newValue: ValueType) => {
        if (!deserializedValue || !isEqual(deserializedValue, newValue)) {
          const serializedValue = valueParser.serialize(newValue);
          if (serializedValue === null) {
            removeItem(storage, key);
          } else {
            try {
              setItem(storage, key, JSON.stringify(serializedValue));
            } catch (error) {
              notify(error, {
                name: 'Storage.couldntStringifyValue',
                context: { key },
              });
              throw error;
            }
          }
        }
      },
    ]);
  };

  return useStorageHook;
}

export const booleanParser: IValueParser<boolean> = {
  serialize: x => x,
  deserialize: x => typeof x === 'boolean' ? x : false,
};
